import { toast as myelaToast } from "@myela/ui";

type ToastType = "success" | "error" | "info";

export function showToast(message: string, type: ToastType = "success") {
  switch (type) {
    case "success":
      myelaToast.success(message);
      break;
    case "error":
      myelaToast.error(message);
      break;
    case "info":
      myelaToast.info(message);
      break;
  }
}

function flatten(node: unknown): string {
  if (node == null || node === false || node === true) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(flatten).join("");
  if (typeof node === "object" && node && "props" in (node as { props?: unknown })) {
    return flatten((node as { props: { children?: unknown } }).props?.children);
  }
  return "";
}

type SonnerArg = string | { title?: unknown; description?: unknown; [key: string]: unknown };

function callMyela(method: "success" | "error" | "info" | "message", arg: SonnerArg, opts?: { description?: unknown }) {
  let message: string;
  if (typeof arg === "string") {
    message = arg;
  } else if (arg && typeof arg === "object") {
    const t = flatten((arg as { title?: unknown }).title);
    const d = flatten((arg as { description?: unknown }).description);
    message = [t, d].filter(Boolean).join(" — ") || t || d || "";
  } else {
    message = "";
  }
  if (opts?.description) {
    const d = flatten(opts.description);
    if (d) message = message ? `${message} — ${d}` : d;
  }
  if (method === "message") {
    myelaToast.info(message);
  } else {
    myelaToast[method](message);
  }
}

interface ToastFn {
  (arg: SonnerArg, opts?: { description?: unknown }): void;
  success: (arg: SonnerArg, opts?: { description?: unknown }) => void;
  error: (arg: SonnerArg, opts?: { description?: unknown }) => void;
  info: (arg: SonnerArg, opts?: { description?: unknown }) => void;
  message: (arg: SonnerArg, opts?: { description?: unknown }) => void;
  warning: (arg: SonnerArg, opts?: { description?: unknown }) => void;
  loading: (arg: SonnerArg, opts?: { description?: unknown }) => void;
  dismiss: (id?: string | number) => void;
}

const toast = ((arg: SonnerArg, opts?: { description?: unknown }) => callMyela("message", arg, opts)) as ToastFn;
toast.success = (arg, opts) => callMyela("success", arg, opts);
toast.error = (arg, opts) => callMyela("error", arg, opts);
toast.info = (arg, opts) => callMyela("info", arg, opts);
toast.message = (arg, opts) => callMyela("message", arg, opts);
toast.warning = (arg, opts) => callMyela("error", arg, opts);
toast.loading = (arg, opts) => callMyela("info", arg, opts);
toast.dismiss = () => {};

export { toast };

type CompatToastInput = {
  title?: unknown;
  description?: unknown;
  variant?: "default" | "destructive" | string;
};

function compatToast(input: CompatToastInput | string) {
  if (typeof input === "string") {
    myelaToast.success(input);
    return;
  }
  const t = flatten(input.title);
  const d = flatten(input.description);
  const message = [t, d].filter(Boolean).join(" — ") || t || d || "";
  if (input.variant === "destructive") {
    myelaToast.error(message);
  } else {
    myelaToast.success(message);
  }
}

export function useToast() {
  return {
    toast: compatToast,
    dismiss: () => {},
  };
}
